import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import {
    Products,
    useModal,
    noticeError,
    trackHeapEvent,
    getReferrerURL,
    getAffiliateData,
    navigateWindowTo,
    GiftOfferIncentiveModal,
    AFFILIATE_QUERY_PARAM_KEYS,
    GIFT_OFFER_LEAD_QUERY_PARAMS,
    LightboxFullScreenTakeoverStyles,
} from '@ratehub/base-ui';

import trackHeapEventForLoans from '../functions/trackHeapEventForLoans';
import trackHeapLoanRedirectEvent from '../functions/trackHeapLoanRedirectEvent';


/**
 * Creates a callback for applying for a loan.
 * When the loan includes a gift offer it will open the gift offer modal before proceeding with normal processing.
 * @returns {(function({
 *      loan: {LoanShape},
 *      [onApplyNow: onApplyNowCallback],
 *      [applyURLOverride: string],
 *      [provinceCode: string],
 *      [enableRedirect: boolean],
 *  })
 *      : (null|undefined))|*}
 */
function useApplyForLoanCallback() {
    const modal = useModal();

    return useCallback(
        /**
         * To be invoked when a user is applying for a loan so that it will open the gift offer modal when needed.
         * @param {LoanShape} loan
         * @param {GiftOfferShape} [loan.giftOffer]
         * @param {onApplyNowCallback} [onApplyNow]
         * @param {string} [applyURLOverride]
         * @param {string} [provinceCode]
         * @param {boolean} [enableRedirect]
         * @param {string} [componentName]
         * @returns {null}
         */
        ({
            loan,
            onApplyNow,
            applyURLOverride,
            provinceCode,
            enableRedirect = true,
            componentName,
        }) => {
            if (!loan) {
                noticeError(new RangeError('`loan` argument to loan callback must not be empty.'));

                return null;
            }

            trackHeapEventForLoans(loan);

            const {
                slug,
                detailsURL,
                provider,
                applyRedirectURL,
                isMonetized,
                isSponsored,
                hasReferrerOffer,
                giftOffer,
            } = loan;

            // alternatively, there may be a gift offer that a 3rd party deals with instead of us
            const isRatehubGiftOffer = !!giftOffer && !giftOffer.is3rdPartyFulfillment;
            // TODO this assumes in the future Loans will be referral eligible
            //      when it is, implement isLoanEligibleForReferralOffer()
            const isReferralEligible = hasReferrerOffer;   // isLoanEligibleForReferralOffer({ isRatehubGiftOffer, hasReferralOffer });

            // do the standard onApplyNow() action when there is no gift card offer AND it is not referral eligible,
            // navigate if enableRedirect, and then we are done; no modal will open
            if (!isRatehubGiftOffer && !isReferralEligible) {
                trackHeapEvent('Loan conversion attempt', {
                    href: detailsURL,
                    slug: slug,
                    provider: provider.name,
                    monetized: isMonetized.toString(),
                    sponsored: isSponsored.toString(),
                });

                trackHeapLoanRedirectEvent({
                    referrerURL: getReferrerURL(),
                    affiliateId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId],
                    loan,
                });

                onApplyNow?.(loan);
                if (enableRedirect) {
                    navigateWindowTo(applyURLOverride ?? applyRedirectURL, {
                        isNewTab: true,
                        queryParams: {
                            ...(componentName && { component_name: componentName }),
                            ...(provinceCode && { province: provinceCode }),
                            sponsored: isSponsored,
                        },
                        ...(componentName && { component_name: componentName }),
                    });
                }

                return null;
            }


            // If we did not exit early with the return directly above:
            //  • there IS a gift offer OR a referral offer, AND
            //  • we have been asked to show it (by context or by experiment)
            // If this is invoked, we know they explicitly accepted or declined the offer.
            // They did NOT cancel, and we are about to send them to the provider’s site.
            function handleAcceptOrDecline(additionalInfo) {
                modal.close(modalId);

                const { email, firstName, lastName, isOfferAccepted } = additionalInfo;

                trackHeapLoanRedirectEvent({
                    referrerURL: getReferrerURL(),
                    affiliateId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId],
                    additionalInfo,
                    loan,
                });

                onApplyNow?.(loan, isOfferAccepted);
                if (enableRedirect) {
                    navigateWindowTo(applyURLOverride ?? applyRedirectURL, {
                        isNewTab: true,
                        queryParams: {
                            ...(provinceCode && { province: provinceCode }),
                            ...(componentName && { component_name: componentName }),
                            ...(isOfferAccepted && {
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.email]: email,
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.firstName]: firstName,
                                // make sure to send an empty string to confirm intentional blankness
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.lastName]: lastName ?? '',
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.isGiftCardLead]: isOfferAccepted,
                            }),
                            sponsored: isSponsored,
                        },
                        ...(componentName && { component_name: componentName }),
                    });
                }
            }

            // We use the modal’s layer id to close the modal programmatically
            const modalId = modal.open({
                isContentFullWidth: true,
                showBranding: false,
                verticalAlignment: 'flex-start',

                style: LightboxFullScreenTakeoverStyles,

                // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
                screenReaderLabel: isRatehubGiftOffer
                    ? (
                        <FormattedMessage
                            id="loans.gift-offer.screen-reader"
                            defaultMessage="Loan gift offer"
                        />
                    )
                    : (
                        <FormattedMessage
                            id="loans.referral-offer.screen-reader"
                            defaultMessage="Referral offer bonus"
                        />
                    ),

                children: (
                    <GiftOfferIncentiveModal
                        giftOffer={loan.giftOffer}
                        providerName={loan.provider.name}
                        productType={Products.LOANS}
                        hasNewsletterOffer={false}
                        onAcceptOrDecline={handleAcceptOrDecline}
                        isReferralEligible={isReferralEligible}
                    />
                ),
            });
        },
        [ modal ],
    );
}


/**
 * @callback onApplyNowCallback
 * @param {LoanShape} loan - the selected loan
 * @param {boolean} [isOfferAccepted=false] - has the user accepted the offer?
 */

export default useApplyForLoanCallback;
